<template>
    <QueryLoaderGuard
        :is-pending="isPending"
        :is-error="isError"
    >
        <div
            v-if="data"
            class="status-over-time-chart"
        >
            <v-chart
                class="status-over-time-chart__chart"
                :option="option"
                autoresize
            />
        </div>
    </QueryLoaderGuard>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { EChartsOption } from 'echarts'
import { BarChart, LineChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
    TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { ConditionStatus, SUMMARY_STATUS } from '@/configuration/status/condition-status'
import { useConditionStatusYearlyQuery } from '@/api/queries/transformers/portfolio-trends/useConditionStatusYearlyQuery'
import { CHART_ICON } from '@/utils/chart-utils'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import { ColorType } from '@/configuration/color/color-type'
import { useTheme } from '@/composables/useTheme'
import {
    getChartLabelBackgroundColor,
    getChartLabelColor,
    getChartToolboxInactiveColor,
} from '@/utils/echarts-theme'

use([
    TitleComponent,
    LineChart,
    BarChart,
    CanvasRenderer,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
])

const props = defineProps<{
    plantSids: number[]
}>()

const { plantSids } = toRefs(props)

const { t } = useI18n()
const { data, isPending, isError } = useConditionStatusYearlyQuery(plantSids)
const { isDarkMode } = useTheme()

const MIN_VALUE_TO_SHOW_LABEL = 4

enum ChartViewType {
    Line,
    Bar,
    BarStacked,
}

const selectedViewType = ref<ChartViewType>(ChartViewType.BarStacked)
const isPercentageValues = computed(() => selectedViewType.value === ChartViewType.BarStacked)

type ViewTypeConfig = {
    ['tooltip.valueFormatter']: (value: string) => string,
    ['yAxis.axisLabel.formatter']: string,
    ['yAxis.max']: number | undefined,
    ['xAxis.boundaryGap']: boolean,
    // Series
    ['series.type']: 'line' | 'bar',
    ['series.stack']: string,
    ['series.label.position']: 'top' | 'inside',
    ['series.label.backgroundColor']: string,
    ['series.label.formatter']: (params: CallbackDataParams) => string
}

const VIEW_TYPE_CONFIG = computed((): Record<ChartViewType, ViewTypeConfig> => ({
    [ChartViewType.Line]: {
        ['tooltip.valueFormatter']: (value) => `${value}`,
        ['yAxis.axisLabel.formatter']: '{value}',
        ['yAxis.max']: undefined,
        ['xAxis.boundaryGap']: false,
        ['series.type']: 'line',
        ['series.stack']: '',
        ['series.label.position']: 'top',
        ['series.label.backgroundColor']: '',
        ['series.label.formatter']: (params) => `${params.data}`,
    },
    [ChartViewType.Bar]: {
        ['tooltip.valueFormatter']: (value) => `${value}`,
        ['yAxis.axisLabel.formatter']: '{value}',
        ['yAxis.max']: undefined,
        ['xAxis.boundaryGap']: true,
        ['series.type']: 'bar',
        ['series.stack']: '',
        ['series.label.position']: 'top',
        ['series.label.backgroundColor']: '',
        ['series.label.formatter']: (params) => `${params.data}`,
    },
    [ChartViewType.BarStacked]: {
        ['tooltip.valueFormatter']: (value) => `${value}%`,
        ['yAxis.axisLabel.formatter']: '{value}%',
        ['yAxis.max']: 100,
        ['xAxis.boundaryGap']: true,
        ['series.type']: 'bar',
        ['series.stack']: 'total',
        ['series.label.position']: 'inside',
        ['series.label.backgroundColor']: getChartLabelBackgroundColor(isDarkMode.value),
        ['series.label.formatter']: (params) => {
            const value = params.data as number

            if (value > MIN_VALUE_TO_SHOW_LABEL) {
                return `${value}%`
            }

            return ''
        },
    },
}))

const series = computed(() => {
    let normalData = data.value?.numberOfNormal?.map((e: number) => e || '-')
    let inferiorData = data.value?.numberOfInferior?.map((e: number) => e || '-')
    let criticalData = data.value?.numberOfCritical?.map((e: number) => e || '-')
    let noActionData = data.value?.numberOfNoAction?.map((e: number) => e || '-')

    if (isPercentageValues.value) {
        normalData =
            data.value?.numberOfNormal?.map((e: number, index: number) => {
                const total =
                    e + (data.value?.numberOfInferior?.[index] || 0) + (data.value?.numberOfCritical?.[index] || 0) + (data.value?.numberOfNoAction?.[index] || 0)
                return e ? +((e / total) * 100).toFixed(1) : '-'
            }) || []

        inferiorData =
            data.value?.numberOfInferior?.map((e: number, index: number) => {
                const total =
                    (data.value?.numberOfNormal?.[index] || 0) + e + (data.value?.numberOfCritical?.[index] || 0) + (data.value?.numberOfNoAction?.[index] || 0)
                return e ? +((e / total) * 100).toFixed(1) : '-'
            }) || []

        criticalData =
            data.value?.numberOfCritical?.map((e: number, index: number) => {
                const total =
                    (data.value?.numberOfNormal?.[index] || 0) + (data.value?.numberOfInferior?.[index] || 0) + e + (data.value?.numberOfNoAction?.[index] || 0)
                return e ? +((e / total) * 100).toFixed(1) : '-'
            }) || []

        noActionData =
            data.value?.numberOfNoAction?.map((e: number, index: number) => {
                const total =
                    (data.value?.numberOfNormal?.[index] || 0) + (data.value?.numberOfInferior?.[index] || 0) + (data.value?.numberOfCritical?.[index] || 0) + e
                return e ? +((e / total) * 100).toFixed(1) : '-'
            }) || []
    }

    return [
        {
            ...SUMMARY_STATUS[ConditionStatus.Normal],
            data: normalData ?? [],
        },
        {
            ...SUMMARY_STATUS[ConditionStatus.Inferior],
            data: inferiorData ?? [],
        },
        {
            ...SUMMARY_STATUS[ConditionStatus.Critical],
            data: criticalData ?? [],
        },
        {
            ...SUMMARY_STATUS[ConditionStatus.NoAction],
            data: noActionData ?? [],
        },
    ]
})

const option = computed((): EChartsOption => {
    const options: EChartsOption = {
        title: {
            show: false,
            text: t('transformers.portfolioTrends.portfolioStatusOverTime.headline'),
        },
        toolbox: {
            show: true,
            itemSize: 16,
            itemGap: 14,
            showTitle: true,
            feature: {
                mySwitchToLineChart: {
                    title: t('chart.action.switchToLineChart'),
                    icon: CHART_ICON.LINE,
                    iconStyle: {
                        color: selectedViewType.value === ChartViewType.Line ? ColorType.Primary : getChartToolboxInactiveColor(isDarkMode.value),
                    },
                    onclick: () => {
                        selectedViewType.value = ChartViewType.Line
                    },
                },
                mySwitchToBarChart: {
                    title: t('chart.action.switchToBarChart'),
                    icon: CHART_ICON.BAR,
                    iconStyle: {
                        color: selectedViewType.value === ChartViewType.Bar ? ColorType.Primary : getChartToolboxInactiveColor(isDarkMode.value),
                    },
                    onclick: () => {
                        selectedViewType.value = ChartViewType.Bar
                    },
                },
                mySwitchToPercentageView: {
                    title: t('chart.action.switchToStackedView'),
                    icon: CHART_ICON.STACK,
                    iconStyle: {
                        color: selectedViewType.value === ChartViewType.BarStacked ? ColorType.Primary : getChartToolboxInactiveColor(isDarkMode.value),
                    },
                    onclick: () => {
                        selectedViewType.value = ChartViewType.BarStacked
                    },
                },
                saveAsImage: {
                    icon: CHART_ICON.SAVE,
                    title: t('chart.action.saveAsImage'),
                },
            },
            iconStyle: {
                color: getChartToolboxInactiveColor(isDarkMode.value),
                borderColor: 'none',
            },
            emphasis: {
                iconStyle: {
                    color: ColorType.Primary,
                    borderColor: 'none',
                },
            },
        },
        tooltip: {
            trigger: 'axis',
            confine: true,
            axisPointer: {
                type: 'shadow',
            },
            valueFormatter: (value) => VIEW_TYPE_CONFIG.value[selectedViewType.value]['tooltip.valueFormatter'](value.toString()),
        },
        legend: {},
        grid: {
            top: 100,
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true,
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: VIEW_TYPE_CONFIG.value[selectedViewType.value]['yAxis.axisLabel.formatter'],
            },
            max: VIEW_TYPE_CONFIG.value[selectedViewType.value]['yAxis.max'],
        },
        xAxis: {
            type: 'category',
            boundaryGap: VIEW_TYPE_CONFIG.value[selectedViewType.value]['xAxis.boundaryGap'],
            data: data.value?.years ?? [],
        },
        aria: {
            enabled: true,
            decal: {
                show: true,
            },
        },
        series: [],
    }

    series.value.forEach((series) => {
        if (!Array.isArray(options.series)) {
            return
        }

        options.series.push({
            name: t(series.name),
            type: VIEW_TYPE_CONFIG.value[selectedViewType.value]['series.type'],
            stack: VIEW_TYPE_CONFIG.value[selectedViewType.value]['series.stack'],
            color: series.color,
            itemStyle: {
                decal: series.pattern,
            },
            label: {
                show: true,
                color: getChartLabelColor(isDarkMode.value),
                fontWeight: 'bold',
                position: VIEW_TYPE_CONFIG.value[selectedViewType.value]['series.label.position'],
                backgroundColor: VIEW_TYPE_CONFIG.value[selectedViewType.value]['series.label.backgroundColor'],
                formatter: (params: CallbackDataParams) => VIEW_TYPE_CONFIG.value[selectedViewType.value]['series.label.formatter'](params),
            },
            data: series.data,
        })
    })

    return options
})
</script>

<style scoped lang="scss">
.status-over-time-chart {
    &__chart {
        width: 100%;
        height: 500px;
    }
}
</style>

<style lang="scss">
.container {
    &__name {
        font-size: $default-font-size;
        color: $pbl-text;
        font-weight: 500;
    }

    &__content {
        margin: 10px 0 0;

        &__item-div {
            margin: 10px 0 0;
            display: flex;
            gap: 16px;
            align-items: flex-start;

            &__color-div {
                display: flex;
                gap: 10px;
                align-items: center;

                &__color-circle {
                    display: inline-block;
                    border-radius: 10px;
                    width: 10px;
                    height: 10px;
                }

                &__series-name {
                    font-size: $default-font-size;
                    color: $pbl-text !important;
                    font-weight: 400;
                    line-height: 18px;
                }
            }

            &__tooltip {
                font-size: $default-font-size;
                color: $pbl-text;
                font-weight: 500;
                line-height: 18px;
                max-width: 200px;
                white-space: normal;
            }
        }
    }
}
</style>
